/**
 * OracleSecurityEvents — Founder-only privacy-enforcement dashboard.
 *
 * Oracle quietly observes, classifies, protects, reports. Denied requests
 * are grouped per actor and classified (benign inquiry → protected request
 * → repeated protected → suspicious probing) so patterns surface without
 * treating every mistake as an attack.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Shield, Loader2, ChevronDown, ChevronRight, Eye, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { adminOracleSecurityEvents } from "../../lib/api";

const CLASSES = {
  benign_inquiry:     { label: "BENIGN INQUIRY",     color: "#3FB950" },
  protected_request:  { label: "PROTECTED REQUEST",  color: "#E0A526" },
  repeated_protected: { label: "REPEATED PROTECTED", color: "#E8722C" },
  suspicious_probing: { label: "SUSPICIOUS PROBING", color: "#D92D20" },
};

const REASON_LABEL = {
  privacy_personal: "personal data (other member)",
  privacy_internal: "system internals",
  capability_denied: "above clearance",
  self_scope_no_member: "no member session",
};

const _fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const TrendBars = ({ trend }) => {
  const max = Math.max(1, ...trend.map((t) => t.total));
  return (
    <div className="flex items-end gap-[2px] h-16" data-testid="secevents-trend">
      {trend.map((t) => (
        <div key={t.date} className="flex-1 flex flex-col justify-end" title={`${t.date}: ${t.total} denied${t.suspicious ? ` · ${t.suspicious} suspicious` : ""}`}>
          {t.suspicious > 0 && (
            <div style={{ height: `${(t.suspicious / max) * 100}%`, background: "#D92D20", minHeight: t.suspicious ? 2 : 0 }} />
          )}
          <div style={{ height: `${((t.total - t.suspicious) / max) * 100}%`, background: "#454038", minHeight: t.total - t.suspicious ? 2 : 0 }} />
        </div>
      ))}
    </div>
  );
};

const GroupRow = ({ g }) => {
  const [open, setOpen] = useState(false);
  const cls = CLASSES[g.classification] || CLASSES.protected_request;
  return (
    <div className="border border-[#2D2A26]"
         style={g.classification === "suspicious_probing" ? { borderColor: "#D92D20" } : undefined}
         data-testid={`secevents-group-${g.actor}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#141310] transition-colors">
        {open ? <ChevronDown size={11} className="text-[#7B7168] shrink-0" />
              : <ChevronRight size={11} className="text-[#7B7168] shrink-0" />}
        <span className="font-mono text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5 shrink-0"
              style={{ background: cls.color, color: "#0a0907" }}>{cls.label}</span>
        <span className="text-[#E4DFD3] text-sm truncate">{g.actor_label}</span>
        <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-[#7B7168] shrink-0">{g.role}</span>
        <span className="ml-auto font-mono text-[10px] text-[#A39E93] shrink-0">
          {g.count} event{g.count !== 1 ? "s" : ""}
        </span>
        <span className="font-mono text-[9px] text-[#7B7168] shrink-0 hidden sm:inline">{_fmt(g.last_at)}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-[#2D2A26] bg-[#12110e]">
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 font-mono text-[10px] text-[#A39E93]">
            <div>First event: <span className="text-[#E4DFD3]">{_fmt(g.first_at)}</span></div>
            <div>Last event: <span className="text-[#E4DFD3]">{_fmt(g.last_at)}</span></div>
            <div className="sm:col-span-2">Reasons: {Object.entries(g.reasons || {}).map(([r, n]) => (
              <span key={r} className="mr-3 text-[#C9C2B4]">{REASON_LABEL[r] || r} ×{n}</span>
            ))}</div>
            <div className="sm:col-span-2">Intents: <span className="text-[#C9C2B4]">{(g.intents || []).join(", ")}</span></div>
          </div>
          {(g.sample_queries || []).length > 0 && (
            <div className="mt-2">
              <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168] mb-1">SAMPLE QUERIES (REFUSED BEFORE RETRIEVAL)</div>
              {g.sample_queries.map((q, i) => (
                <div key={i} className="font-mono text-[10px] text-[#C9C2B4] truncate">"{q}"</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const OracleSecurityEvents = ({ token, userToken }) => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d = days) => {
    setLoading(true);
    try { setData(await adminOracleSecurityEvents(token, d, userToken)); }
    catch (e) {
      toast.error(e?.response?.status === 403
        ? "Oracle Security Events are Founder-only."
        : "Failed to load security events");
    } finally { setLoading(false); }
  }, [token, userToken, days]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const groups = data?.groups || [];
  const windowKey = days === 1 ? "24h" : days === 7 ? "7d" : "30d";
  const suspicious = useMemo(
    () => groups.filter((g) => g.classification === "suspicious_probing"),
    [groups]);

  return (
    <div className="space-y-5" data-testid="oracle-security-events-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Shield size={16} className="text-[#9FA8D4]" />
        <h2 className="font-display text-lg text-[#E4DFD3] tracking-wide">ORACLE SECURITY EVENTS</h2>
        <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#7B7168]">
          observes · classifies · protects · reports
        </span>
        <div className="ml-auto flex gap-1">
          {[[1, "24H"], [7, "7 DAYS"], [30, "30 DAYS"]].map(([d, label]) => (
            <button key={d} type="button" onClick={() => setDays(d)}
                    data-testid={`secevents-range-${d}`}
                    className={`px-2 py-1 border font-mono text-[8px] uppercase tracking-[0.2em] transition-colors ${days === d ? "border-[#9FA8D4] text-[#9FA8D4]" : "border-[#2D2A26] text-[#7B7168] hover:text-[#A39E93]"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-10 justify-center text-[#7B7168] font-mono text-xs">
          <Loader2 size={14} className="animate-spin" /> COMPILING SECURITY INTELLIGENCE…
        </div>
      ) : !data ? (
        <div className="border border-[#2D2A26] px-4 py-8 text-center font-mono text-[11px] text-[#7B7168]">
          Security events unavailable.
        </div>
      ) : (
        <>
          {suspicious.length > 0 && (
            <div className="border border-[#D92D20] px-3 py-2 font-mono text-[10px] text-[#D92D20]"
                 data-testid="secevents-suspicious-banner">
              <AlertTriangle size={10} className="inline mr-1.5" />
              {suspicious.length} actor{suspicious.length !== 1 ? "s" : ""} showing a suspicious probing pattern.
              All requests were refused before retrieval — no data was exposed.
            </div>
          )}

          {/* Summary — classification × window */}
          <div className="border border-[#2D2A26]" data-testid="secevents-summary">
            <div className="grid grid-cols-4 gap-px bg-[#2D2A26]">
              {Object.entries(CLASSES).map(([key, c]) => (
                <div key={key} className="bg-[#0F0E0B] px-3 py-2.5 text-center"
                     data-testid={`secevents-summary-${key}`}>
                  <div className="font-display text-xl" style={{ color: c.color }}>
                    {(summary[windowKey] || {})[key] ?? 0}
                  </div>
                  <div className="font-mono text-[7px] uppercase tracking-[0.18em] text-[#7B7168] mt-0.5">{c.label}</div>
                  <div className="font-mono text-[7px] text-[#544E45] mt-1">
                    24h {(summary["24h"] || {})[key] ?? 0} · 7d {(summary["7d"] || {})[key] ?? 0} · 30d {(summary["30d"] || {})[key] ?? 0}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trend */}
          <div className="border border-[#2D2A26] px-3 py-2" data-testid="secevents-trend-box">
            <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168] mb-2">
              DENIED-REQUEST TREND — {windowKey.toUpperCase()}
              <span className="ml-3 text-[#544E45]">▮ denied</span>
              <span className="ml-2" style={{ color: "#D92D20" }}>▮ suspicious</span>
            </div>
            <TrendBars trend={data.trend || []} />
          </div>

          {/* Grouped events */}
          <div data-testid="secevents-groups">
            <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168] mb-2">
              EVENTS BY ACTOR — REPEATED EVENTS GROUPED · thresholds: {data.thresholds?.repeat}+ in {data.thresholds?.window_minutes}min escalates
            </div>
            {groups.length === 0 ? (
              <div className="border border-[#2D2A26] px-3 py-6 text-center font-mono text-[10px] text-[#3FB950]"
                   data-testid="secevents-empty">
                <Eye size={12} className="inline mr-1.5" />
                No security events in this window. Oracle is watching.
              </div>
            ) : (
              <div className="space-y-1.5">
                {groups.map((g) => <GroupRow key={g.actor} g={g} />)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default OracleSecurityEvents;
